/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { ToastContainer } from '../components/Toast';
import { ConfirmDialog } from '../components/ConfirmDialog';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
}

export interface ConfirmDialogState {
  type: 'alert' | 'confirm';
  title?: string;
  message: string;
  dialogType?: 'danger' | 'warning' | 'info';
  resolve: (value: boolean) => void;
}

interface UIContextType {
  toast: (message: string, type?: ToastType) => void;
  alert: (message: string, title?: string) => Promise<void>;
  confirm: (message: string, title?: string, dialogType?: 'danger' | 'warning' | 'info') => Promise<boolean>;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const useUI = (): UIContextType => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

interface UIProviderProps {
  children: ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [dialog, setDialog] = useState<ConfirmDialogState | null>(null);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const alert = useCallback((message: string, title: string = 'Notification') => {
    return new Promise<void>((resolve) => {
      setDialog({
        type: 'alert',
        title,
        message,
        resolve: () => {
          setDialog(null);
          resolve();
        },
      });
    });
  }, []);

  const confirm = useCallback(
    (message: string, title: string = 'Confirmation', dialogType: 'danger' | 'warning' | 'info' = 'warning') => {
      return new Promise<boolean>((resolve) => {
        setDialog({
          type: 'confirm',
          title,
          message,
          dialogType,
          resolve: (value: boolean) => {
            setDialog(null);
            resolve(value);
          },
        });
      });
    },
    []
  );

  const contextValue = useMemo(() => ({ toast, alert, confirm }), [toast, alert, confirm]);

  return (
    <UIContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {dialog && (
        <ConfirmDialog
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          dialogType={dialog.dialogType}
          onClose={(value) => dialog.resolve(value)}
        />
      )}
    </UIContext.Provider>
  );
};
